import { request } from '../utils/httpClientTs';
import { goRequest } from '../utils/httpClientTs';
import {
  UserPersonalInfoResponse,
  UserPersonalInfoUpdateRequest,
  UserAddressUpdateRequest,
  UserRelationshipCreateRequest,
  UserRelationshipUpdateRequest,
  UserListParams,
  UserListResponse,
  UserCreateRequest,
  UserCreateResponse,
  UserCommitteesResponse,
  TransferMemberRequest,
  InitiateMemberRequest,
  CheckUserResponse,
  InitiateUserRequest,
  MembershipActionRequest,
  MembershipActionCheckResponse,
  UserSearchResponse,
} from '../types/users';
import { GenericResponse } from '../types/generic';

// User Personal Information Endpoints

export const getUserPersonalInfo = (
  userId: string
): Promise<UserPersonalInfoResponse> => {
  return goRequest<UserPersonalInfoResponse>({
    url: `/users/${userId}/personal-info`,
    method: 'GET',
  });
};

export const updateUserPersonalInfo = (
  userId: string,
  data: UserPersonalInfoUpdateRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/personal-info`,
    method: 'PATCH',
    data: data,
  });
};

// User Address Endpoints

export const createUserAddress = (
  userId: string,
  data: UserAddressUpdateRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/address`,
    method: 'POST',
    data: data,
  });
};

export const updateUserAddress = (
  userId: string,
  addressId: string,
  data: UserAddressUpdateRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/address/${addressId}`,
    method: 'PATCH',
    data: data,
  });
};

export const deleteUserAddress = (
  userId: string,
  addressId: string
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/address/${addressId}`,
    method: 'DELETE',
  });
};

// User Relationship Endpoints

export const createUserRelationship = (
  userId: string,
  data: UserRelationshipCreateRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/relationships`,
    method: 'POST',
    data: data,
  });
};

export const updateUserRelationship = (
  userId: string,
  relationshipId: string,
  data: UserRelationshipUpdateRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/relationships/${relationshipId}`,
    method: 'PUT',
    data: data,
  });
};

export const deleteUserRelationship = (
  userId: string,
  relationshipId: string
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/relationships/${relationshipId}`,
    method: 'DELETE',
  });
};

// Additional User Endpoints (from existing users.js)

export const searchUsers = (params: {
  search?: string;
  member_status?:
    | 'active'
    | 'inactive'
    | 'deceased'
    | 'non-members'
    | 'all'
    | 'all-members';
  spd_chapter_id?: string;
  local_chapter_id?: string;
  page?: number; // 0-based
  per_page?: number;
}): Promise<UserSearchResponse> => {
  let url = '/users/search';
  const searchParams = new URLSearchParams();

  if (params.search) {
    searchParams.append('search', params.search);
  }
  if (params.member_status && params.member_status !== 'all') {
    searchParams.append('member_status', params.member_status);
  }
  if (params.spd_chapter_id) {
    searchParams.append('spd_chapter_id', params.spd_chapter_id);
  }
  if (params.local_chapter_id) {
    searchParams.append('local_chapter_id', params.local_chapter_id);
  }
  if (params.page !== undefined) {
    searchParams.append('page', params.page.toString());
  }
  if (params.per_page) {
    searchParams.append('per_page', params.per_page.toString());
  }

  if (searchParams.toString()) {
    url += `?${searchParams.toString()}`;
  }

  return goRequest<UserSearchResponse>({
    url: url,
    method: 'GET',
  });
};

/** Current user's application permissions (optional scope: local_chapter_id, spd_chapter_id, international). */
export const getMyPermissions = (params?: {
  local_chapter_id?: string;
  spd_chapter_id?: string;
  international?: string;
}): Promise<string[]> => {
  let url = '/users/me/permissions';
  const searchParams = new URLSearchParams();
  if (params?.local_chapter_id) {
    searchParams.append('local_chapter_id', params.local_chapter_id);
  }
  if (params?.spd_chapter_id) {
    searchParams.append('spd_chapter_id', params.spd_chapter_id);
  }
  if (params?.international) {
    searchParams.append('international', params.international);
  }
  if (searchParams.toString()) {
    url += `?${searchParams.toString()}`;
  }
  return goRequest<string[]>({ url, method: 'GET' });
};

export const advancedSearchUsers = (params: {
  first_name?: string;
  last_name?: string;
  member_id?: string;
  email?: string;
  phone?: string;
  page?: number;
  per_page?: number;
}): Promise<UserSearchResponse> => {
  const searchParams = new URLSearchParams();
  if (params.first_name != null && params.first_name !== '') {
    searchParams.append('first_name', params.first_name);
  }
  if (params.last_name != null && params.last_name !== '') {
    searchParams.append('last_name', params.last_name);
  }
  if (params.member_id != null && params.member_id !== '') {
    searchParams.append('member_id', params.member_id);
  }
  if (params.email != null && params.email !== '') {
    searchParams.append('email', params.email);
  }
  if (params.phone != null && params.phone !== '') {
    searchParams.append('phone', params.phone);
  }
  if (params.page !== undefined) {
    searchParams.append('page', params.page.toString());
  }
  if (params.per_page !== undefined) {
    searchParams.append('per_page', params.per_page.toString());
  }
  const query = searchParams.toString();
  const url = query
    ? `/users/advanced-search?${query}`
    : '/users/advanced-search';
  return goRequest<UserSearchResponse>({ url, method: 'GET' });
};

export const getUser = (userId: string): Promise<any> => {
  return request<any>({
    url: `/users/${userId}`,
    method: 'GET',
  });
};

export const updateUser = (
  userId: string,
  data: any
): Promise<GenericResponse> => {
  return request<GenericResponse>({
    url: `/users/${userId}`,
    method: 'PATCH',
    data: data,
  });
};

export const checkUserByEmail = (email: string): Promise<CheckUserResponse> => {
  return goRequest<CheckUserResponse>({
    url: `/users/check?email=${encodeURIComponent(email)}`,
    method: 'GET',
  });
};

export const createUser = (
  data: UserCreateRequest
): Promise<UserCreateResponse> => {
  return goRequest<UserCreateResponse>({
    url: '/users',
    method: 'POST',
    data: data,
  });
};

export const initiateUser = (
  userId: string,
  data: InitiateUserRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/initiate`,
    method: 'POST',
    data: data,
  });
};

// Deprecated - use createUser instead
export const createUsers = (
  data: UserCreateRequest
): Promise<UserCreateResponse> => {
  return request<UserCreateResponse>({
    url: '/users/create',
    method: 'POST',
    data: data,
  });
};

// User List Endpoint
export const getUserList = (
  params: UserListParams
): Promise<UserListResponse> => {
  let url = `/users/?page=${params.page}&page_size=${params.rowsPerPage}`;

  if (params.search) {
    url += `&search=${encodeURIComponent(params.search)}`;
  }

  if (params.userType) {
    url += `&type=${params.userType}`;
  }

  if (params.spd) {
    url += `&spd=${params.spd}`;
  }

  if (params.orderBy) {
    url += `&order_by=${params.orderBy}`;
  }

  if (params.order) {
    url += `&order=${params.order}`;
  }

  return request<UserListResponse>({
    url: url,
    method: 'GET',
  });
};

// Additional user functions for backward compatibility
export const getUserDetail = (data: { id: string }): Promise<any> => {
  return goRequest<any>({
    url: `/users/${data.id}`,
    method: 'GET',
  });
};

export const getUserPermissions = (data: { id: string }): Promise<any> => {
  return request<any>({
    url: `/users/${data.id}/permissions`,
    method: 'GET',
  });
};

export const updateUserPermissions = (data: any): Promise<GenericResponse> => {
  const userId = data.id;
  delete data.id;
  return request<GenericResponse>({
    url: `/users/${userId}/permissions`,
    method: 'PATCH',
    data: data,
  });
};

export const getUserRoles = (): Promise<any> => {
  return request<any>({
    url: `/users/permissions/roles`,
    method: 'GET',
  });
};

export const getUserApplications = (): Promise<any> => {
  return request<any>({
    url: `/users/permissions/applications`,
    method: 'GET',
  });
};

export const getUserReports = (): Promise<any> => {
  return request<any>({
    url: `/users/permissions/reports`,
    method: 'GET',
  });
};

export const getUserCommittees = (data: {
  id: string;
}): Promise<UserCommitteesResponse> => {
  return goRequest<UserCommitteesResponse>({
    url: `/users/${data.id}/committees`,
    method: 'GET',
  });
};

export const getUserNotes = (data: { id: string }): Promise<any> => {
  return request<any>({
    url: `/users/${data.id}/notes`,
    method: 'GET',
  });
};

export const getUserNoteTypes = (): Promise<any> => {
  return request<any>({
    url: '/users/notes/types',
    method: 'GET',
  });
};

export const createUserNote = (data: any): Promise<GenericResponse> => {
  return request<GenericResponse>({
    url: `/users/${data.userId}/notes`,
    method: 'POST',
    data: data.data,
  });
};

export const updateUserNote = (data: any): Promise<GenericResponse> => {
  const noteId = data.noteId;
  delete data.noteId;
  return request<GenericResponse>({
    url: `/users/${data.userId}/notes/${noteId}`,
    method: 'PATCH',
    data: data.data,
  });
};

export const deleteUserNote = (data: any): Promise<GenericResponse> => {
  return request<GenericResponse>({
    url: `/users/${data.userId}/notes/${data.noteId}`,
    method: 'DELETE',
  });
};

export const getUserProjects = (data: { id: string }): Promise<any> => {
  return request<any>({
    url: `/users/${data.id}/projects`,
    method: 'GET',
  });
};

export const getUserUpdateHistory = (data: { id: string }): Promise<any> => {
  return request<any>({
    url: `/users/${data.id}/update_history`,
    method: 'GET',
  });
};

export const transferPost = (
  data: TransferMemberRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/transfer`,
    method: 'POST',
    data: data,
  });
};

export const initiateMember = (
  data: InitiateMemberRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/initiate`,
    method: 'POST',
    data: data,
  });
};

// Additional functions for CreateUser component
export const getRoles = (): Promise<any> => {
  return request<any>({
    url: `/users/permissions/roles`,
    method: 'GET',
  });
};

export const getApplications = (): Promise<any> => {
  return request<any>({
    url: `/users/permissions/applications`,
    method: 'GET',
  });
};

export const getReports = (): Promise<any> => {
  return request<any>({
    url: `/users/permissions/reports`,
    method: 'GET',
  });
};

// Membership Action Endpoints (Transfer/Reinstatement)
export const checkMembershipAction = (
  userId: string,
  data: MembershipActionRequest
): Promise<MembershipActionCheckResponse> => {
  return goRequest<MembershipActionCheckResponse>({
    url: `/users/${userId}/membership-action/check`,
    method: 'POST',
    data: data,
  });
};

export const executeMembershipAction = (
  userId: string,
  data: MembershipActionRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/users/${userId}/membership-action`,
    method: 'POST',
    data: data,
  });
};
